import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { ArrowLeft, Download, FileText } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";

export default function Reports() {
  const [, navigate] = useLocation();

  const [reportType, setReportType] = useState<"weekly" | "monthly">("monthly");
  const [selectedWellId, setSelectedWellId] = useState<string>("");
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { data: wells } = trpc.wells.list.useQuery({ activeOnly: true });

  const { data: monthlyReport, refetch: refetchMonthly } = trpc.reports.monthly.useQuery(
    {
      wellId: parseInt(selectedWellId),
      year: parseInt(year),
      month: parseInt(month),
    },
    { enabled: reportType === "monthly" && !!selectedWellId }
  );

  const { data: weeklyReport, refetch: refetchWeekly } = trpc.reports.weekly.useQuery(
    {
      wellId: parseInt(selectedWellId),
      startDate: new Date(startDate),
      endDate: new Date(endDate),
    },
    { enabled: reportType === "weekly" && !!selectedWellId && !!startDate && !!endDate }
  );

  const handleGenerate = () => {
    if (!selectedWellId) {
      toast.error("Please select a well");
      return;
    }

    if (reportType === "monthly") {
      refetchMonthly();
    } else {
      if (!startDate || !endDate) {
        toast.error("Please select start and end dates");
        return;
      }
      refetchWeekly();
    }
  };

  const exportPDFMutation = trpc.reports.monthlyPDF.useMutation();

  const handleExportPDF = async () => {
    if (!selectedWellId || reportType !== "monthly") {
      toast.error("PDF export is only available for monthly reports");
      return;
    }

    try {
      toast.loading("Generating PDF...");
      const result = await exportPDFMutation.mutateAsync({
        wellId: parseInt(selectedWellId),
        year: parseInt(year),
        month: parseInt(month),
      });

      // Convert base64 to blob
      const byteCharacters = atob(result.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success("PDF exported successfully");
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to generate PDF");
    }
  };

  const handleExportCSV = () => {
    const report = reportType === "monthly" ? monthlyReport : weeklyReport;
    if (!report?.logs || report.logs.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = ["Date", "Time", "Operator", "Meter Start", "Meter End", "Gallons Pumped", "GPM", "Hours", "Remarks"];
    const rows = report.logs.map(log => [
      new Date(log.logDate).toLocaleDateString(),
      log.logTime || "",
      log.operatorInitial || "",
      log.meterReadStart || "",
      log.meterReadEnd || "",
      log.gallonsPumped || "",
      log.gpm || "",
      log.hours || "",
      log.remarks || "",
    ]);

    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${report.well?.wellName || "report"}-${reportType}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success("Report exported successfully");
  };

  const report = reportType === "monthly" ? monthlyReport : weeklyReport;
  // Use server-provided totals instead of client-side calculation
  const totals = report?.totals || null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-xl font-bold">Reports</h1>
          </div>
        </div>
      </header>

      <main className="container py-6 max-w-4xl">
        {/* Quick Action - All Wells Production Report */}
        <Card className="mb-6 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20 border-emerald-200 dark:border-emerald-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              All Wells Production Report
            </CardTitle>
            <CardDescription>
              View monthly production matrix across all wells (Million Gallons)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate("/reports/all-wells-production")} 
              className="w-full"
              variant="default"
            >
              <FileText className="w-4 h-4 mr-2" />
              Open All Wells Monthly Matrix Report
            </Button>
          </CardContent>
        </Card>

        {/* Report Configuration */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Generate Report</CardTitle>
            <CardDescription>Select parameters to generate weekly or monthly reports</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="type">Report Type</Label>
              <Select value={reportType} onValueChange={(v: any) => setReportType(v)}>
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly Report</SelectItem>
                  <SelectItem value="weekly">Weekly Report</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="well">Well</Label>
              <Select value={selectedWellId} onValueChange={setSelectedWellId}>
                <SelectTrigger id="well">
                  <SelectValue placeholder="Select a well" />
                </SelectTrigger>
                <SelectContent>
                  {wells?.map((well) => (
                    <SelectItem key={well.id} value={well.id.toString()}>
                      {well.wellName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {reportType === "monthly" ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="year">Year</Label>
                  <Input
                    id="year"
                    type="number"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="month">Month</Label>
                  <Select value={month} onValueChange={setMonth}>
                    <SelectTrigger id="month">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start">Start Date</Label>
                  <Input
                    id="start"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="end">End Date</Label>
                  <Input
                    id="end"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}

            <Button onClick={handleGenerate} className="w-full">
              <FileText className="w-4 h-4 mr-2" />
              Generate Report
            </Button>
          </CardContent>
        </Card>

        {/* Report Results */}
        {report && report.logs && report.logs.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{report.well?.wellName} - {reportType === "monthly" ? "Monthly" : "Weekly"} Report</CardTitle>
                  <CardDescription>
                    {reportType === "monthly" 
                      ? `${new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}`
                      : `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`
                    }
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {reportType === "monthly" && (
                    <Button variant="outline" onClick={handleExportPDF}>
                      <FileText className="w-4 h-4 mr-2" />
                      Export PDF
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleExportCSV}>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Total Entries</p>
                  <p className="text-2xl font-bold">{totals?.entryCount || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Gallons</p>
                  <p className="text-2xl font-bold">{totals?.totalGallons}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Hours</p>
                  <p className="text-2xl font-bold">{totals?.totalHours}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">MG</p>
                  <p className="text-2xl font-bold">{totals?.totalMG || "0.000"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Avg GPM</p>
                  <p className="text-2xl font-bold">{totals?.averageGPM || "0.00"}</p>
                </div>
              </div>

              {/* Data Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr className="text-left">
                      <th className="py-2 px-2">Date</th>
                      <th className="py-2 px-2">Time</th>
                      <th className="py-2 px-2">Operator</th>
                      <th className="py-2 px-2">Gallons</th>
                      <th className="py-2 px-2">GPM</th>
                      <th className="py-2 px-2">Hours</th>
                      <th className="py-2 px-2">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.logs.map((log) => (
                      <tr key={log.id} className="border-b">
                        <td className="py-2 px-2">{new Date(log.logDate).toLocaleDateString()}</td>
                        <td className="py-2 px-2">{log.logTime || "-"}</td>
                        <td className="py-2 px-2">{log.operatorInitial || "-"}</td>
                        <td className="py-2 px-2">{log.gallonsPumped || "-"}</td>
                        <td className="py-2 px-2">{log.gpm || "-"}</td>
                        <td className="py-2 px-2">{log.hours || "-"}</td>
                        <td className="py-2 px-2 max-w-xs truncate">{log.remarks || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {report && report.logs && report.logs.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No data found for the selected period</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
